import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Container, Paper, Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import NotificationSnackbar from '../components/ui/NotificationSnackbar';
import { CrmBadge, CrmButton, CrmFormDrawer, CrmInput, CrmSelect, CrmSwitch, CrmTabs, CrmTextarea, CrmTooltip } from '../components/ui';
import { useSettings } from '../context/SettingsContext';
import { useConfirm } from '../context/ConfirmContext';
import AddressFields from '../components/settings/AddressFields';
import MediaAssetPicker from '../components/settings/MediaAssetPicker';
import { displayValue, jsonForEditor, normalizeAddress, normalizeOptions, parseJsonValue, slugCode } from '../components/settings/settingsFormUtils';
import '../components/settings/settingsForms.scss';
import {
  deleteBrand, deleteBusinessUnit, deleteConfigurationProfile, deleteLegalEntity, deleteSalesChannel, deleteStorefront, getCommercialResourceDependencies,
  listBrands, listBusinessUnits, listConfigurationProfiles, listLegalEntities, listSalesChannels, listStorefronts,
  saveBrand, saveBusinessUnit, saveConfigurationProfile, saveConfigurationProfileValues, saveLegalEntity, saveSalesChannel, saveStorefront,
} from '../services/commercialContextService';

const ENTITY_CONFIGS = {
  'legal-entities': {
    label: 'Legal Entities', singular: 'legal entity', list: listLegalEntities, save: saveLegalEntity, delete: deleteLegalEntity,
    displayKey: 'legal_name', codeFrom: 'legal_name',
    defaultValues: { legal_name: '', trade_name: '', gstin: '', pan: '', registered_address: {}, billing_address: {}, bank_information_ref: '', configuration_profile_id: '', is_active: true },
    fields: [
      { key: 'is_active', label: 'Active', type: 'switch', group: 'status' },
      { key: 'legal_name', label: 'Legal Name', required: true }, { key: 'trade_name', label: 'Trade Name' },
      { key: 'gstin', label: 'GSTIN' }, { key: 'pan', label: 'PAN' },
      { key: 'registered_address', label: 'Registered Address', type: 'address' },
      { key: 'billing_address', label: 'Billing Address', type: 'address' },
      { key: 'bank_information_ref', label: 'Bank / Payment Information Reference', helperText: 'Optional reference used by document/payment configuration.' },
      { key: 'configuration_profile_id', label: 'Configuration Profile', relation: 'configuration-profiles' },
    ],
    columns: [{ key: 'legal_name', label: 'Legal Name' }, { key: 'trade_name', label: 'Trade Name' }, { key: 'gstin', label: 'GSTIN' }, { key: 'is_active', label: 'Status' }],
  },
  brands: {
    label: 'Brands', singular: 'brand', list: listBrands, save: saveBrand, delete: deleteBrand, displayKey: 'name', codeFrom: 'name',
    defaultValues: { name: '', logo_url: '', logo_media_asset_id: '', website_url: '', support_email: '', support_phone: '', configuration_profile_id: '', is_active: true },
    fields: [
      { key: 'is_active', label: 'Active', type: 'switch', group: 'status' }, { key: 'name', label: 'Name', required: true },
      { key: 'logo_media_asset_id', label: 'Brand Logo', type: 'media' }, { key: 'website_url', label: 'Website URL', type: 'url' },
      { key: 'support_email', label: 'Support Email', type: 'email' }, { key: 'support_phone', label: 'Support Phone', type: 'tel' },
      { key: 'configuration_profile_id', label: 'Configuration Profile', relation: 'configuration-profiles' },
    ],
    columns: [{ key: 'name', label: 'Name' }, { key: 'website_url', label: 'Website' }, { key: 'support_email', label: 'Support Email' }, { key: 'is_active', label: 'Status' }],
  },
  'business-units': {
    label: 'Business Units', singular: 'business unit', list: listBusinessUnits, save: saveBusinessUnit, delete: deleteBusinessUnit, displayKey: 'name', codeFrom: 'name',
    defaultValues: { name: '', brand_id: '', legal_entity_id: '', default_sales_channel_id: '', default_storefront_id: '', configuration_profile_id: '', is_active: true },
    fields: [
      { key: 'is_active', label: 'Active', type: 'switch', group: 'status' }, { key: 'name', label: 'Name', required: true },
      { key: 'brand_id', label: 'Brand', relation: 'brands', required: true }, { key: 'legal_entity_id', label: 'Legal Entity', relation: 'legal-entities', required: true },
      { key: 'default_sales_channel_id', label: 'Default Sales Channel', relation: 'sales-channels' }, { key: 'default_storefront_id', label: 'Default Storefront', relation: 'storefronts' },
      { key: 'configuration_profile_id', label: 'Configuration Profile', relation: 'configuration-profiles' },
    ],
    columns: [{ key: 'name', label: 'Name' }, { key: 'brand_id', label: 'Brand', relation: 'brands' }, { key: 'legal_entity_id', label: 'Legal Entity', relation: 'legal-entities' }, { key: 'is_active', label: 'Status' }],
  },
  'sales-channels': {
    label: 'Sales Channels', singular: 'sales channel', list: listSalesChannels, save: saveSalesChannel, delete: deleteSalesChannel, displayKey: 'name', codeFrom: 'name',
    defaultValues: { name: '', channel_type: '', is_active: true },
    fields: [
      { key: 'is_active', label: 'Active', type: 'switch', group: 'status' }, { key: 'name', label: 'Name', required: true },
      { key: 'channel_type', label: 'Channel Type', required: true, options: ['WEBSITE', 'MARKETPLACE', 'DIRECT', 'POS', 'PHONE', 'OTHER'] },
    ],
    columns: [{ key: 'name', label: 'Name' }, { key: 'channel_type', label: 'Channel Type' }, { key: 'is_active', label: 'Status' }],
  },
  storefronts: {
    label: 'Storefronts', singular: 'storefront', list: listStorefronts, save: saveStorefront, delete: deleteStorefront, displayKey: 'name', codeFrom: 'name',
    defaultValues: { name: '', domain: '', brand_id: '', business_unit_id: '', sales_channel_id: '', configuration_profile_id: '', is_active: true },
    fields: [
      { key: 'is_active', label: 'Active', type: 'switch', group: 'status' }, { key: 'name', label: 'Name', required: true },
      { key: 'domain', label: 'Domain', required: true }, { key: 'brand_id', label: 'Brand', relation: 'brands', required: true },
      { key: 'business_unit_id', label: 'Business Unit', relation: 'business-units', required: true }, { key: 'sales_channel_id', label: 'Sales Channel', relation: 'sales-channels', required: true },
      { key: 'configuration_profile_id', label: 'Configuration Profile', relation: 'configuration-profiles' },
    ],
    columns: [{ key: 'name', label: 'Name' }, { key: 'domain', label: 'Domain' }, { key: 'brand_id', label: 'Brand', relation: 'brands' }, { key: 'is_active', label: 'Status' }],
  },
  'configuration-profiles': {
    label: 'Configuration Profiles', singular: 'configuration profile', list: listConfigurationProfiles, save: saveConfigurationProfile, delete: deleteConfigurationProfile,
    displayKey: 'name', codeFrom: 'name',
    defaultValues: { name: '', description: '', configuration_values: '{}', is_active: true },
    fields: [
      { key: 'is_active', label: 'Active', type: 'switch', group: 'status' }, { key: 'name', label: 'Name', required: true },
      { key: 'description', label: 'Description', type: 'textarea', wide: true },
      { key: 'configuration_values', label: 'Configuration Values', type: 'json', wide: true, helperText: 'Advanced profile values as a JSON object. These values are inherited by records assigned to this profile.' },
    ],
    columns: [{ key: 'name', label: 'Name' }, { key: 'description', label: 'Description' }, { key: 'is_active', label: 'Status' }],
  },
};

const ENTITY_KEYS = Object.keys(ENTITY_CONFIGS);
const buildInitialForm = (entityKey) => ({ ...ENTITY_CONFIGS[entityKey].defaultValues });
const relationLabel = (entityKey, row) => entityKey === 'legal-entities' ? (row.legal_name || row.trade_name || row.name) : row.name;

export default function CommercialContextSettings({ embedded = false, initialEntity = 'legal-entities', taxOnly = false }) {
  const { reloadOperationalContext } = useSettings();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryEntity = searchParams.get('entity');
  const initialEntityKey = ENTITY_CONFIGS[initialEntity] ? initialEntity : 'legal-entities';
  const resolvedEntity = ENTITY_CONFIGS[queryEntity] ? queryEntity : initialEntityKey;
  const [activeEntity, setActiveEntity] = useState(resolvedEntity);
  const [rowsByEntity, setRowsByEntity] = useState(() => Object.fromEntries(ENTITY_KEYS.map((key) => [key, []])));
  const [loading, setLoading] = useState(false); const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false); const [editingId, setEditingId] = useState(null);
  const [formValues, setFormValues] = useState(buildInitialForm(initialEntity));
  const [serverError, setServerError] = useState(''); const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  const activeConfig = ENTITY_CONFIGS[activeEntity] || ENTITY_CONFIGS['legal-entities'];
  const activeFields = taxOnly
    ? activeConfig.fields.filter((field) => ['is_active', 'legal_name', 'trade_name', 'gstin', 'pan', 'registered_address', 'billing_address', 'bank_information_ref'].includes(field.key))
    : activeConfig.fields;

  useEffect(() => {
    const nextEntity = ENTITY_CONFIGS[queryEntity] ? queryEntity : initialEntityKey;
    setActiveEntity((current) => current === nextEntity ? current : nextEntity);
  }, [queryEntity, initialEntityKey]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.all(ENTITY_KEYS.map(async (entityKey) => [entityKey, await ENTITY_CONFIGS[entityKey].list()]));
      setRowsByEntity(Object.fromEntries(results.map(([key, rows]) => [key, Array.isArray(rows) ? rows : []])));
    } catch (error) {
      setNotification({ open: true, message: error?.response?.data?.error || 'Failed to load business structure.', severity: 'error' });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { if (ENTITY_CONFIGS[initialEntity]) setActiveEntity(initialEntity); }, [initialEntity]);

  const openCreateDialog = () => { setEditingId(null); setFormValues(buildInitialForm(activeEntity)); setServerError(''); setDialogOpen(true); };
  const openEditDialog = (row) => {
    setEditingId(row.id);
    const normalizedRow = activeEntity === 'legal-entities'
      ? { ...row, registered_address: normalizeAddress(row.registered_address), billing_address: normalizeAddress(row.billing_address) }
      : row;
    setFormValues({ ...buildInitialForm(activeEntity), ...normalizedRow, configuration_values: activeEntity === 'configuration-profiles' ? jsonForEditor(row.configuration_values || {}, {}) : row.configuration_values, logo_media_asset_id: '' });
    setServerError(''); setDialogOpen(true);
  };
  const closeDialog = () => { if (!saving) setDialogOpen(false); };
  const handleFieldChange = (key, value) => setFormValues((previous) => ({ ...previous, [key]: value }));

  const handleSubmit = async () => {
    const required = activeFields.filter((field) => field.required && !formValues[field.key]);
    if (required.length) { setServerError(`Please complete: ${required.map((field) => field.label).join(', ')}`); return; }
    setSaving(true); setServerError('');
    try {
      if (editingId) {
        const ok = await confirm({ title: `Update ${activeConfig.singular}?`, message: 'This structural change can affect new transactions and configuration resolution immediately. Existing transaction snapshots remain unchanged.', confirmText: 'Save changes' });
        if (!ok) return;
      }
      const payload = { ...formValues };
      if (activeEntity === 'legal-entities') {
        payload.registered_address = normalizeAddress(payload.registered_address);
        payload.billing_address = normalizeAddress(payload.billing_address);
      }
      const configurationValues = activeEntity === 'configuration-profiles' ? parseJsonValue(payload.configuration_values, {}) : null;
      delete payload.configuration_values;
      delete payload.logo_media_asset_id;
      if (!editingId) payload.code = slugCode(payload[activeConfig.codeFrom] || activeConfig.singular);
      if (editingId) payload.id = editingId;
      Object.keys(payload).forEach((key) => { if (payload[key] === '') payload[key] = null; });
      const saved = await activeConfig.save(payload);
      if (activeEntity === 'configuration-profiles') await saveConfigurationProfileValues(saved.id, configurationValues || {});
      setNotification({ open: true, message: `${activeConfig.singular} ${editingId ? 'updated' : 'created'} successfully.`, severity: 'success' });
      setDialogOpen(false); await Promise.all([loadData(), reloadOperationalContext?.()]);
    } catch (error) {
      setServerError(error?.response?.data?.error || error?.response?.data?.details || error?.message || `Failed to save ${activeConfig.singular}.`);
    } finally { setSaving(false); }
  };

  const handleDelete = async (row) => {
    let dependencies = [];
    try {
      const result = await getCommercialResourceDependencies(activeEntity, row.id);
      dependencies = Array.isArray(result?.dependencies) ? result.dependencies : [];
    } catch (_) {
      // The backend still enforces referential integrity if dependency inspection is unavailable.
    }
    const blocking = dependencies.filter((dependency) => dependency.blocks_delete);
    const cascading = dependencies.filter((dependency) => !dependency.blocks_delete);
    const dependencySummary = [
      blocking.length ? `Deletion is blocked by ${blocking.map((dependency) => `${dependency.count} ${dependency.label}`).join(', ')}.` : '',
      cascading.length ? `Deleting will also affect ${cascading.map((dependency) => `${dependency.count} ${dependency.label} (${dependency.delete_rule.toLowerCase()})`).join(', ')}.` : '',
    ].filter(Boolean).join(' ');
    const ok = await confirm({
      title: `Delete ${activeConfig.singular}?`,
      message: `${dependencySummary ? `${dependencySummary} ` : ''}Delete “${row[activeConfig.displayKey] || 'this record'}”? Deactivation is preferred for records already used operationally.`,
      confirmText: blocking.length ? 'Cannot delete' : 'Delete', cancelText: 'Cancel',
    });
    if (!ok || blocking.length) return;
    try {
      await activeConfig.delete(row.id); await Promise.all([loadData(), reloadOperationalContext?.()]);
      setNotification({ open: true, message: `${activeConfig.singular} deleted successfully.`, severity: 'success' });
    } catch (error) {
      setNotification({ open: true, message: error?.response?.data?.error || 'Delete was blocked because this record is still referenced.', severity: 'error' });
    }
  };

  const renderField = (field) => {
    if (field.type === 'switch') return <div key={field.key} className="settings-toggle-row settings-form-grid__wide"><CrmSwitch checked={Boolean(formValues[field.key])} onChange={(event) => handleFieldChange(field.key, event.target.checked)} label={field.label} helperText="Inactive records are excluded from new operational resolution and selectors." /></div>;
    if (field.type === 'address') return <AddressFields key={field.key} label={field.label} value={formValues[field.key]} onChange={(value) => handleFieldChange(field.key, value)} />;
    if (field.type === 'media') return <MediaAssetPicker key={field.key} label={field.label} value={formValues[field.key]} currentUrl={formValues.logo_url} mediaType="IMAGE" onChange={(id, asset) => setFormValues((previous) => ({ ...previous, [field.key]: id, logo_url: asset?.url || previous.logo_url }))} />;
    if (field.relation || field.options) {
      const relationRows = (rowsByEntity[field.relation] || []).filter((item) => Number(item.is_active) !== 0 || Number(item.id) === Number(formValues[field.key]));
      const options = field.options ? field.options.map((value) => ({ value, label: value.replace(/_/g, ' ') })) : normalizeOptions(relationRows, field.relation === 'legal-entities' ? ['legal_name', 'trade_name', 'name'] : ['name', 'legal_name', 'description']);
      return <CrmSelect key={field.key} label={field.label} required={field.required} value={formValues[field.key] ?? ''} onChange={(event) => handleFieldChange(field.key, event.target.value)}><option value="">Select {field.label.toLowerCase()}</option>{options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</CrmSelect>;
    }
    if (field.type === 'json') return <CrmTextarea key={field.key} className="settings-form-grid__wide" label={field.label} rows={8} helperText={field.helperText || 'Valid JSON object'} value={formValues[field.key] ?? '{}'} onChange={(event) => handleFieldChange(field.key, event.target.value)} />;
    if (field.type === 'textarea') return <CrmTextarea key={field.key} className={field.wide ? 'settings-form-grid__wide' : ''} label={field.label} rows={4} value={formValues[field.key] ?? ''} onChange={(event) => handleFieldChange(field.key, event.target.value)} />;
    return <CrmInput key={field.key} label={field.label} type={field.type || 'text'} required={field.required} helperText={field.helperText} value={formValues[field.key] ?? ''} onChange={(event) => handleFieldChange(field.key, event.target.value)} />;
  };

  const content = <>
    {!embedded && <Typography variant="h4" fontWeight={700}>Business Structure</Typography>}
    <Typography color="text.secondary" sx={{ mt: 1 }}>
      {taxOnly ? 'Manage legal and tax identity used by newly issued documents.' : 'Manage the legal entities, brands, business units, channels, storefronts and configuration inheritance used by the commerce architecture.'}
    </Typography>
    {!taxOnly && (
      <CrmTabs
        className="settings-entity-tabs"
        items={ENTITY_KEYS.map((key) => ({ id: key, label: ENTITY_CONFIGS[key].label }))}
        activeId={activeEntity}
        onChange={(nextEntity) => {
          setActiveEntity(nextEntity);
          const nextSearchParams = new URLSearchParams(searchParams.toString());
          nextSearchParams.set('section', 'structure');
          nextSearchParams.set('entity', nextEntity);
          setSearchParams(nextSearchParams, { replace: true });
        }}
        ariaLabel="Business structure settings"
      />
    )}
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 2 }}>
      <Typography variant="h6">{taxOnly ? 'Tax & Legal Identity' : activeConfig.label}</Typography>
      <CrmButton variant="primary" size="small" onClick={openCreateDialog}>Add {activeConfig.singular}</CrmButton>
    </Box>
    {activeEntity === 'configuration-profiles' && !taxOnly ? <Alert severity="info" sx={{ mb: 2 }}>Configuration Profiles are the named inheritance layer assigned to legal entities, brands, business units and storefronts. Forms now select these names instead of exposing database IDs.</Alert> : null}
    {loading ? <Alert severity="info">Loading records…</Alert> : (
      <div className="settings-structure-table crm-data-table-wrap">
        <table className="crm-data-table">
          <thead>
            <tr>
              {activeConfig.columns.map((column) => <th key={column.key} scope="col">{column.label}</th>)}
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(rowsByEntity[activeEntity] || []).length ? (rowsByEntity[activeEntity] || []).map((row) => (
              <tr key={row.id}>
                {activeConfig.columns.map((column) => (
                  <td key={`${row.id}-${column.key}`}>
                    {column.key === 'is_active'
                      ? <CrmBadge tone={row.is_active ? 'success' : 'neutral'}>{row.is_active ? 'Active' : 'Inactive'}</CrmBadge>
                      : column.relation
                        ? relationLabel(column.relation, (rowsByEntity[column.relation] || []).find((item) => Number(item.id) === Number(row[column.key])) || {}) || '—'
                        : displayValue(row[column.key])}
                  </td>
                ))}
                <td className="crm-data-table__actions-cell">
                  <span className="crm-row-action-group">
                    {activeEntity === 'storefronts' && Boolean(row.is_active) ? (
                      <CrmTooltip content="Open storefront experience">
                        <CrmButton iconOnly size="small" variant="tertiary" className="crm-row-action" onClick={() => navigate(`/storefronts/${encodeURIComponent(row.code)}/experience`)} aria-label="Open storefront experience">
                          <LanguageOutlinedIcon />
                        </CrmButton>
                      </CrmTooltip>
                    ) : null}
                    <CrmTooltip content={`Edit ${activeConfig.singular}`}>
                      <CrmButton iconOnly size="small" variant="tertiary" className="crm-row-action" onClick={() => openEditDialog(row)} aria-label={`Edit ${activeConfig.singular}`}>
                        <EditOutlinedIcon />
                      </CrmButton>
                    </CrmTooltip>
                    <CrmTooltip content={`Delete ${activeConfig.singular}`}>
                      <CrmButton iconOnly size="small" variant="tertiary" className="crm-row-action crm-row-action--danger" onClick={() => handleDelete(row)} aria-label={`Delete ${activeConfig.singular}`}>
                        <DeleteOutlineIcon />
                      </CrmButton>
                    </CrmTooltip>
                  </span>
                </td>
              </tr>
            )) : (
              <tr><td colSpan={activeConfig.columns.length + 1}><div className="settings-structure-table__empty">No records configured yet.</div></td></tr>
            )}
          </tbody>
        </table>
      </div>
    )}
  </>;

  const drawer = <CrmFormDrawer open={dialogOpen} onClose={closeDialog} onSubmit={handleSubmit} canSave={!saving} mode={editingId ? 'edit' : 'create'} title={`${editingId ? 'Edit' : 'Add'} ${activeConfig.singular}`} subtitle={taxOnly ? 'Legal and tax identity used for new documents.' : 'Configuration changes apply to future resolution and transactions.'} size="lg">
    <p className="settings-form-note">Codes and database IDs are managed internally. Select related records by name; deactivate records rather than deleting them when they are already operationally referenced.</p>
    {serverError ? <div className="crm-form-drawer__error" role="alert">{serverError}</div> : null}
    <div className="settings-form-grid">{activeFields.map(renderField)}</div>
  </CrmFormDrawer>;

  const notices = <NotificationSnackbar {...notification} onClose={() => setNotification((previous) => ({ ...previous, open: false }))} />;
  return embedded ? <>{content}{drawer}{notices}</> : <Container maxWidth="lg" sx={{ py: 3 }}><Paper sx={{ p: 3 }}>{content}</Paper>{drawer}{notices}</Container>;
}
